'use client';
import { supportsAbortController, supportsRequestStreams, supportsFormData, supportsResponseStreams } from '@s7/platform';
import { isString, isUndefined, invariantSync, invariant } from '@s7/utils';
import { isResponse, isRequest } from '../utils/guards.js';
import { getInternalOptions } from './utils/getInternalOptions.js';
import { getComposedInput } from './utils/getComposedInput.js';
import { getInputCredentials } from './utils/getInputCredentials.js';
import { maxSafeTimeout } from '../domain/constants.js';
import { TimeoutRangeError } from '../domain/errors/TimeoutRangeError.js';
import { HTTPError } from '../domain/errors/HTTPError.js';
import { ResponseStreamsSupportError } from '../domain/errors/ResponseStreamsSupportError.js';
import { watchDownloadStream } from './utils/watchDownloadStream.js';
import { getUnknownOptions } from './utils/getUnknownOptions.js';
import { TimeoutError } from '../domain/errors/TimeoutError.js';
import { JSONParseError } from '../domain/errors/JSONParseError.js';

function asyncGeneratorStep(gen, resolve, reject, _next, _throw, key, arg) {
    try {
        var info = gen[key](arg);
        var value = info.value;
    } catch (error) {
        reject(error);
        return;
    }
    if (info.done) {
        resolve(value);
    } else {
        Promise.resolve(value).then(_next, _throw);
    }
}
function _async_to_generator(fn) {
    return function() {
        var self = this, args = arguments;
        return new Promise(function(resolve, reject) {
            var gen = fn.apply(self, args);
            function _next(value) {
                asyncGeneratorStep(gen, resolve, reject, _next, _throw, "next", value);
            }
            function _throw(err) {
                asyncGeneratorStep(gen, resolve, reject, _next, _throw, "throw", err);
            }
            _next(undefined);
        });
    };
}
function _extends() {
    _extends = Object.assign || function(target) {
        for(var i = 1; i < arguments.length; i++){
            var source = arguments[i];
            for(var key in source){
                if (Object.prototype.hasOwnProperty.call(source, key)) {
                    target[key] = source[key];
                }
            }
        }
        return target;
    };
    return _extends.apply(this, arguments);
}
/**
 * Функция собирает параметры fetch на основе `input` и `options`,
 * с которыми далее будет выполнен запрос, а также создаёт набор методов,
 * с определёнными внутри них инвариантами.
 *
 * Отвечает за применение хуков.
 * */ const Client = (input, options)=>{
    const internalOptions = getInternalOptions(input, options);
    const composedInput = getComposedInput(input, options.prefixUrl);
    const credentials = getInputCredentials(input);
    //// Далее важен порядок операций инициализации.
    /* Если в `input` присутствуют credentials, добавляем их к параметрам запроса */ if (credentials) {
        internalOptions.credentials = credentials;
    }
    let abortController;
    /* Если есть поддержка AbortController, создаём его и заменяем signal в параметрах запроса. */ if (supportsAbortController) {
        abortController = new globalThis.AbortController();
        if (internalOptions.signal) {
            const originalSignal = internalOptions.signal;
            internalOptions.signal.addEventListener('abort', ()=>{
                abortController.abort(originalSignal.reason);
            });
        }
        internalOptions.signal = abortController.signal;
    }
    /*
     * Если есть поддержка Streaming API устанавливаем режим
     * попеременной передачи и приёма для дальнейшей работы с ReadableStream.
     * */ if (supportsRequestStreams) {
        // @ts-expect-error - Просроченные типы.
        internalOptions.duplex = 'half';
    }
    let initialRequest = new globalThis.Request(composedInput, internalOptions);
    if (internalOptions.searchParams) {
        const textSearchParams = isString(internalOptions.searchParams) ? internalOptions.searchParams.replace(/^\?/, '') : // тем не менее создание URLSearchParams выполняется корректно за счёт внутренних приведений типов.
        new URLSearchParams(internalOptions.searchParams).toString();
        const url = initialRequest.url.replace(/(?:\?.*?)?(?=#|$)/, '?' + textSearchParams);
        const bodyIsFormData = internalOptions.body instanceof globalThis.FormData;
        const bodyIsURLSearchParams = internalOptions.body instanceof URLSearchParams;
        /*
         * Для обеспечения правильной границы формы,
         * заголовок Content-Type должен удаляться каждый раз,
         * когда новый запрос создается из другого.
         */ if ((supportsFormData && bodyIsFormData || bodyIsURLSearchParams) && internalOptions.headers && !internalOptions.headers.has('content-type')) {
            initialRequest.headers.delete('content-type');
        }
        /*
         * Копирование `self.request` обязательно, так как в противном случае
         * свойство `duplex` оказывается не определено и выбрасывается ошибка.
         * */ initialRequest = new globalThis.Request(new globalThis.Request(url, _extends({}, initialRequest)), internalOptions);
    }
    /*
     * Если собираемся передать содержимое, добавляем требующиеся заголовки и `body`
     * в параметры запроса.
     */ if (!isUndefined(internalOptions.json)) {
        internalOptions.body = JSON.stringify(internalOptions.json);
        var _internalOptions_headers_get;
        initialRequest.headers.set('content-type', (_internalOptions_headers_get = internalOptions.headers.get('content-type')) != null ? _internalOptions_headers_get : 'application/json');
        initialRequest = new globalThis.Request(initialRequest, {
            body: internalOptions.body
        });
    }
    const self = {
        request: initialRequest,
        options: internalOptions,
        execute
    };
    function fetch() {
        return _fetch.apply(this, arguments);
    }
    function _fetch() {
        _fetch = ////
        /**
     * Выполняет запрос, используя Fetch API.
     * Перед запросом выполняет beforeRequest-middleware.
     *
     * По timeout выбрасывает TimeoutError.
     * */ _async_to_generator(function*() {
            /*
         * Возвращается только в том случае, если middleware вернул Response,
         * Должно вызывать раннюю остановку.
         */ const result = yield applyBeforeRequestHooks();
            if (isResponse(result)) {
                return result;
            }
            const requestClone = self.request.clone();
            const nonRequestOptions = getUnknownOptions(self.request, internalOptions);
            /* Выполнение без timeout */ if (self.options.timeout === false) {
                return self.options.fetch(requestClone, nonRequestOptions);
            }
            /* Выполнение c timeout */ return new Promise((resolve, reject)=>{
                const timeoutId = setTimeout(()=>{
                    if (abortController) {
                        abortController.abort();
                    }
                    reject(new TimeoutError(requestClone));
                }, self.options.timeout);
                void self.options.fetch(requestClone, nonRequestOptions).then(resolve).catch(reject).then(()=>{
                    clearTimeout(timeoutId);
                });
            });
        });
        return _fetch.apply(this, arguments);
    }
    /**
     * При клонировании модифицирует `response.clone`, чтобы копии также декорировались;
     * Обрабатывает случай с ответом 204 (пустой body).
     * */ function decorateResponse(response) {
        response.json = /*#__PURE__*/ _async_to_generator(function*() {
            const text = yield response.text();
            if (response.status === 204) {
                try {
                    return self.options.parseJson(text);
                } catch (e) {
                    return null;
                }
            }
            try {
                return self.options.parseJson(text);
            } catch (e) {
                throw new JSONParseError(text, response, self.request, self.options);
            }
        });
        const makeClone = response.clone.bind(response);
        response.clone = ()=>{
            return decorateResponse(makeClone());
        };
        return response;
    }
    function applyBeforeRequestHooks() {
        return _applyBeforeRequestHooks.apply(this, arguments);
    }
    function _applyBeforeRequestHooks() {
        _applyBeforeRequestHooks = /**
     * Мутирует self.request.
     * Если middleware вернул Response, возвращает его.
     * Иначе возвращает undefined.
     * */ _async_to_generator(function*() {
            for (const middleware of self.options.middlewares.beforeRequest){
                const result = yield middleware(self.request, self.options);
                if (isRequest(result)) {
                    self.request = result;
                    break;
                }
                if (isResponse(result)) {
                    return result;
                }
            }
        });
        return _applyBeforeRequestHooks.apply(this, arguments);
    }
    function applyAfterResponseHooks(response) {
        return _applyAfterResponseHooks.apply(this, arguments);
    }
    function _applyAfterResponseHooks() {
        _applyAfterResponseHooks = /** Не мутирует Response. */ _async_to_generator(function*(response) {
            /* Не клонируем, если нет middleware */ let modifiedResponse = response;
            for (const middleware of self.options.middlewares.afterResponse){
                const responseCandidate = decorateResponse(modifiedResponse.clone());
                const nextResponse = yield middleware(self.request, self.options, responseCandidate);
                if (isResponse(nextResponse)) {
                    modifiedResponse = nextResponse;
                }
            }
            return modifiedResponse;
        });
        return _applyAfterResponseHooks.apply(this, arguments);
    }
    function applyBeforeErrorHooks(httpError) {
        return _applyBeforeErrorHooks.apply(this, arguments);
    }
    function _applyBeforeErrorHooks() {
        _applyBeforeErrorHooks = /** Мутирует Error */ _async_to_generator(function*(httpError) {
            let error = httpError;
            for (const middleware of self.options.middlewares.beforeError){
                error = yield middleware(error);
            }
            return error;
        });
        return _applyBeforeErrorHooks.apply(this, arguments);
    }
    function execute() {
        return _execute.apply(this, arguments);
    }
    function _execute() {
        _execute = /**
     * Выполняет запрос, применяя все middleware.
     * */ _async_to_generator(function*() {
            const unsafeTimeoutRange = !!self.options.timeout && self.options.timeout > maxSafeTimeout;
            invariantSync(unsafeTimeoutRange, ()=>new TimeoutRangeError());
            /*
         * Откладываем выполнение запроса, чтобы `body`-методы Response
         * могли устанавливать заголовок `Accept`.
         *
         * @example
         * // Заметьте, мы не ожидаем разрешения promise после запроса,
         * // поэтому нам требуется отложить запрос, чтобы вызовом body-метода
         * // установить заголовок.
         * client.get('/some').json() // Accept: application/json
         * client.get('/some').blob() // Accept: */ /*
         * */ yield Promise.resolve();
            const originalResponse = yield fetch();
            const response = decorateResponse((yield applyAfterResponseHooks(originalResponse)));
            const requestFailed = !response.ok && self.options.throwHttpErrors;
            yield invariant(requestFailed, ()=>{
                return applyBeforeErrorHooks(new HTTPError(response, self.request, self.options));
            });
            /*
         * Если передано `onDownloadProgress`, требуется использовать Streams API
         * для отслеживания прогресса получения данных.
         * */ if (self.options.onDownloadProgress) {
                invariantSync(!supportsResponseStreams, ()=>new ResponseStreamsSupportError());
                return watchDownloadStream(response.clone(), self.options.onDownloadProgress);
            }
            return response;
        });
        return _execute.apply(this, arguments);
    }
    return self;
};

export { Client };
//# sourceMappingURL=Client.js.map
